import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Database } from "@/integrations/supabase/types";

type AppRole = Database['public']['Enums']['app_role'];
type Profile = Database['public']['Tables']['profiles']['Row'];

export const useUserRoles = (userId?: string) => {
  const queryClient = useQueryClient();

  const { data: userProfile, isLoading } = useQuery({
    queryKey: ['user-profile', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, user_role')
        .eq('id', userId || '')
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!userId
  });

  const { data: isAdmin, isLoading: isAdminLoading } = useQuery({
    queryKey: ['is-admin', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('is_admin_simple', { user_id_param: userId });
      
      if (error) throw error;
      return data as boolean;
    },
    enabled: !!userId
  });

  const assignRole = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: AppRole }) => {
      const { data, error } = await supabase
        .from('profiles')
        .update({ user_role: role })
        .eq('id', userId);
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      queryClient.invalidateQueries({ queryKey: ['is-admin'] });
    }
  });

  const removeRole = useMutation({
    mutationFn: async ({ userId }: { userId: string; role: AppRole }) => {
      const { error } = await supabase
        .from('profiles')
        .update({ user_role: 'user' })
        .eq('id', userId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      queryClient.invalidateQueries({ queryKey: ['is-admin'] });
    }
  });

  return {
    userRoles: userProfile ? [{ role: userProfile.user_role }] : [],
    isAdmin: isAdmin || false,
    isLoading: isLoading || isAdminLoading,
    assignRole,
    removeRole
  };
};